import {
  ActivityIndicator,
  Platform,
  Pressable,
  ScrollView,
  View,
  type NativeScrollEvent,
  type LayoutChangeEvent,
} from "@-/com.npmjs.react-native";
import { useSafeAreaInsets } from "@-/com.npmjs.react-native-safe-area-context";
import {
  FLOATING_TAB_BAR_BOTTOM_GAP_PX,
  FLOATING_TAB_BAR_HEIGHT_PX,
} from "@-/dev.improve.haniverse.lib.primary-navigation-strip";
import { TopSheet, useTopSheetMotion } from "@-/dev.improve.haniverse.components.ui.top-sheet";
import { useSharedValue } from "@-/com.npmjs.react-native-reanimated";
import { useContextSelectors } from "@-/com.npmjs.use-context-selectors";
import { useContext, useEffect, useRef, useState, type ReactNode } from "react";
import { Surface } from "@-/dev.improve.haniverse.components.ui.surface";
import { useGlassHeaderInset } from "@-/dev.improve.haniverse.components.ui.header-scroll";
import {
  isScrollEdgeEffectSupported,
  ScrollEdgeTopExtensionContext,
  useScrollEdgeScrollerRegistration,
} from "@-/dev.improve.haniverse.modules.scroll-edge-effect";
import { Text } from "@-/dev.improve.haniverse.components.ui.text";
import { Icon } from "@-/dev.improve.haniverse.components.ui.icon";
import { useRouteBodyState } from "@-/dev.improve.haniverse.lib.navigation.route-body-state";
import type { MorphemeInfoPanelAccordionSectionDescriptor } from "@-/dev.improve.haniverse.routes.nav.app._components.morpheme.panels._components.panel-content._components.morpheme-info-panel-accordion";
import { MorphemeInfoPanelContext } from "@-/dev.improve.haniverse.routes.nav.app._components.morpheme.panels._components.panel-content._context";

const HANDLE_HEIGHT = 32;
const CONTEXT_GAP = 8;
const WORD_SURFACE_GAP = 9;
/** The words renderer supplies its own 8px top padding inside the context scroller. */
const WORD_SURFACE_TOP_PADDING = 8;

function isNearEnd(event: NativeScrollEvent) {
  return event.contentOffset.y + event.layoutMeasurement.height >= event.contentSize.height - 160;
}

/**
 * Where the native tabs float over this tab's content: `clearance` runs from the content's bottom
 * edge to the top of their platter, and `gapBelow` is the empty band beneath the platter.
 */
function useNativeTabsBottomGeometry(fallbackClearance: number) {
  const { bottom } = useSafeAreaInsets();
  // Each iOS native tab wraps its content in its own safe-area provider, whose bottom inset spans
  // the whole bar: the platter and the gutter below it.
  if (Platform.OS === "ios" && bottom >= FLOATING_TAB_BAR_HEIGHT_PX) {
    return { clearance: bottom, gapBelow: bottom - FLOATING_TAB_BAR_HEIGHT_PX };
  }
  return { clearance: fallbackClearance, gapBelow: FLOATING_TAB_BAR_BOTTOM_GAP_PX };
}

/**
 * Shared top-anchored dictionary sheet over context examples. Closed, it keeps the morpheme's core
 * details visible with the related surface's header peeking out beneath them, while the examples
 * scroll below. Swiping down reveals the related surface to its end, or as far as the native tabs
 * allow: the gap left above them matches the gap below them.
 */
export function DictionaryTopSheet({
  contextSections,
  expandedContent,
  kind,
  onEndReached,
  overviewContent,
  renderExpandedHeader,
  renderHeader,
}: {
  contextSections: MorphemeInfoPanelAccordionSectionDescriptor[];
  expandedContent: ReactNode;
  kind: "grapheme" | "word";
  onEndReached?: () => void;
  /** Follows the related surface; reached by scrolling the expanded sheet. */
  overviewContent: ReactNode;
  /** The related surface's header, which peeks out beneath the closed sheet's header. */
  renderExpandedHeader: (expand: () => void) => ReactNode;
  renderHeader: (onLayout: (event: LayoutChangeEvent) => void) => ReactNode;
}) {
  const noun = kind === "grapheme" ? "Character" : "Word";
  const related = kind === "grapheme" ? "compounds" : "occurrences";
  const context = kind === "grapheme" ? "words" : "examples";
  const positionLabels = [`${noun} summary`, `Expanded ${related}`];
  const [setIsScrolled, bottomInset] = useContextSelectors(
    MorphemeInfoPanelContext,
    (c) => c.setIsScrolled,
    (c) => c.floatingTabBottomInset,
  );
  const [isExpanded, setIsExpanded] = useRouteBodyState(`${kind}-dictionary-sheet-expanded`, false);
  const position = isExpanded ? 1 : 0;
  const [availableHeight, setAvailableHeight] = useState(600);
  const [headerHeight, setHeaderHeight] = useState(84);
  const [expandedHeaderHeight, setExpandedHeaderHeight] = useState(36);
  const [expandedContentHeight, setExpandedContentHeight] = useState(0);
  const scrollOffset = useSharedValue(0);
  const requestedContentHeight = useRef(-1);
  const bodyScroll = useRef<ScrollView>(null);
  const glassInset = useGlassHeaderInset();
  const topExtension = useContext(ScrollEdgeTopExtensionContext);
  const viewportExtension = glassInset === undefined ? 0 : topExtension;
  // The toolbar height already includes the 9px gutter below its buttons. Reserve it inside
  // the scroller so the sheet surface is flush with the header and content can pass behind glass.
  const contentInset = glassInset ?? 0;
  const restingInset = contentInset - viewportExtension;
  useScrollEdgeScrollerRegistration(glassInset !== undefined && isScrollEdgeEffectSupported);
  // The header, then the related surface's header strip between its 1pt borders.
  const compactHeight = headerHeight + expandedHeaderHeight + 2;
  const nativeTabs = useNativeTabsBottomGeometry(bottomInset);
  // Expanded, the sheet's bottom edge stops as far above the native tabs' platter as the platter
  // sits above the bottom edge, leaving the remaining examples visible between them.
  const maximum = Math.max(
    0,
    availableHeight -
      nativeTabs.clearance -
      nativeTabs.gapBelow -
      (restingInset + compactHeight + HANDLE_HEIGHT + 1),
  );
  // Reveal the related surface down to its end when it fits.
  // Include the related surface's bottom border and outer gap in the revealed sheet height.
  const relatedSurfaceEnd = 1 + (kind === "grapheme" ? 4 : 12);
  const expanded = Math.min(expandedContentHeight + relatedSurfaceEnd, maximum);
  const motion = useTopSheetMotion({
    maximum: expanded,
    onPositionChange: (next) => setIsExpanded(next === 1),
    position,
    scrollOffset,
  });

  useEffect(() => {
    if (!isExpanded) {
      bodyScroll.current?.scrollTo({ animated: false, y: 0 });
      scrollOffset.set(0);
    }
  }, [isExpanded, scrollOffset]);

  return (
    <View
      className="min-h-0 flex-1"
      onLayout={(event) => setAvailableHeight(event.nativeEvent.layout.height)}
      testID={`${kind}-dictionary-details`}
    >
      <TopSheet
        handle={
          <Pressable
            accessibilityActions={[
              { label: `Reveal ${related}`, name: "increment" },
              { label: `Show ${context}`, name: "decrement" },
            ]}
            accessibilityHint={`Swipe down to reveal ${related}. Swipe up to make more room for ${context}.`}
            accessibilityLabel={`${noun} details sheet`}
            accessibilityRole="adjustable"
            accessibilityValue={{
              max: 1,
              min: 0,
              now: position,
              text: positionLabels[position],
            }}
            className="h-8 items-center justify-center"
            onAccessibilityAction={(event) =>
              setIsExpanded(event.nativeEvent.actionName === "increment")
            }
            onPress={() => setIsExpanded((current) => !current)}
            testID={`${kind}-dictionary-sheet-handle`}
          >
            <View className="h-1 w-10 rounded-full bg-muted-foreground/40" />
          </Pressable>
        }
        motion={motion}
        style={{ top: -viewportExtension }}
        testID={`${kind}-dictionary-top-sheet`}
        viewportInset={contentInset + compactHeight}
      >
        <ScrollView
          bounces={false}
          contentContainerStyle={{ paddingBottom: HANDLE_HEIGHT, paddingTop: contentInset }}
          nestedScrollEnabled
          onScroll={(event) => {
            const nativeEvent = event.nativeEvent;
            scrollOffset.set(nativeEvent.contentOffset.y);
            if (
              isExpanded &&
              isNearEnd(nativeEvent) &&
              requestedContentHeight.current !== nativeEvent.contentSize.height
            ) {
              requestedContentHeight.current = nativeEvent.contentSize.height;
              onEndReached?.();
            }
          }}
          ref={bodyScroll}
          scrollEnabled={isExpanded}
          scrollEventThrottle={32}
          scrollIndicatorInsets={{ bottom: HANDLE_HEIGHT, top: contentInset }}
          testID={`${kind}-dictionary-sheet-scroll`}
        >
          {renderHeader((event) => setHeaderHeight(event.nativeEvent.layout.height))}
          <Surface
            className={kind === "grapheme" ? "mx-1 mb-1 bg-surface" : "mx-3 mb-3 bg-background"}
            testID={`${kind}-dictionary-related`}
          >
            <View onLayout={(event) => setExpandedHeaderHeight(event.nativeEvent.layout.height)}>
              {renderExpandedHeader(() => setIsExpanded(true))}
            </View>
            <View
              accessibilityElementsHidden={!isExpanded}
              importantForAccessibility={isExpanded ? "auto" : "no-hide-descendants"}
              onLayout={(event) => setExpandedContentHeight(event.nativeEvent.layout.height)}
            >
              {expandedContent}
            </View>
          </Surface>
          <View
            accessibilityElementsHidden={!isExpanded}
            importantForAccessibility={isExpanded ? "auto" : "no-hide-descendants"}
          >
            {overviewContent}
          </View>
        </ScrollView>
      </TopSheet>
      {/* Context stays beneath the closed sheet; the expanded sheet covers it rather than pushing. */}
      <View
        className="min-h-0 flex-1"
        style={{
          paddingTop:
            restingInset +
            compactHeight +
            HANDLE_HEIGHT +
            (kind === "grapheme" ? WORD_SURFACE_GAP - WORD_SURFACE_TOP_PADDING : CONTEXT_GAP) +
            1,
        }}
        testID={`${kind}-dictionary-${context}-view`}
      >
        {contextSections.map((section) => (
          <View className="min-h-0 flex-1" key={section.headerKey}>
            {/* Grapheme words list needs no sticky title — the sheet above already frames the tab. */}
            {kind === "word" ? (
              <View className="flex-row items-center justify-between px-3 py-2">
                <Text className="text-sm font-semibold">{section.title}</Text>
                <View className="flex-row">
                  {section.isLoading ? <ActivityIndicator size="small" /> : null}
                  {section.headerActions?.map((action) => (
                    <Pressable
                      accessibilityLabel={action.label}
                      accessibilityRole="button"
                      accessibilityState={{ selected: action.selected }}
                      className="w-8 items-center"
                      key={action.label}
                      onPress={action.onPress}
                    >
                      <Icon
                        className={action.selected ? "text-primary" : "text-muted-foreground"}
                        name={action.iconName}
                        size={18}
                      />
                    </Pressable>
                  ))}
                </View>
              </View>
            ) : null}
            <ScrollView
              contentContainerStyle={{ paddingBottom: nativeTabs.clearance }}
              keyboardShouldPersistTaps="handled"
              onScroll={(event) => setIsScrolled(event.nativeEvent.contentOffset.y > 0)}
              scrollEventThrottle={32}
              testID={`${kind}-dictionary-${context}-scroll`}
            >
              {section.renderBody()}
            </ScrollView>
          </View>
        ))}
      </View>
    </View>
  );
}
